import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { createCoupon, getProducts, getCategories, getWarehouses, getAudiencesList } from '../config/api';
import useIsMobile from '../hooks/useIsMobile';

const CreateCoupon = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [couponData, setCouponData] = useState({
    code: '',
    type: 'FIXED',
    value: '',
    min_cart_value: '',
    max_discount: '',
    applicable_product_ids: [],
    applicable_category_ids: [],
    applicable_warehouse_ids: [],
    applicable_gst_nums: [],
    gst_number: '',
    valid_for_initial: '',
    usage_limit: '',
    valid_from: '',
    valid_till: '',
    active: true,
    waive_off_delivery: false
  });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [selectedKits, setSelectedKits] = useState([]);
  const [filteredProductOptions, setFilteredProductOptions] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [kitOptions, setKitOptions] = useState([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(true);
  const [audienceOptions, setAudienceOptions] = useState([]);
  const [selectedAudience, setSelectedAudience] = useState(null);
  const [loadingAudiences, setLoadingAudiences] = useState(true);

  useEffect(() => {
    loadProducts();
    loadCategories();
    loadWarehouses();
    loadAudiences();
    // Set default dates
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(today.getMonth() + 1);
    
    setCouponData(prev => ({
      ...prev,
      valid_from: today.toISOString().split('T')[0],
      valid_till: nextMonth.toISOString().split('T')[0]
    }));
  }, []);

  useEffect(() => {
    setFilteredProductOptions(productOptions);
  }, [productOptions]);

  const loadProducts = async () => {
    try {
      const productListRes = await getProducts();
      const allItems = productListRes.data || [];
      setProducts(allItems);

      const prodOptions = [];
      const kitsOpts = [];

      allItems.forEach((item) => {
        if (item.isKit) {
          // Kit products go into both product options (for category 4 filtering) and kit options
          kitsOpts.push({
            value: item.id,
            label: `${item.name} (Custom Kit)`,
            product: { ...item, categoryId: 4 },
          });
          prodOptions.push({
            value: item.id,
            label: `${item.name} (Custom Kit)`,
            product: { ...item, categoryId: 4 },
            skuCode: item.skuCode,
          });
        } else {
          prodOptions.push({
            value: item.id,
            label: `${item.skuCode} - ${item.name}`,
            product: item,
            skuCode: item.skuCode,
          });
        }
      });

      setProductOptions(prodOptions);
      setKitOptions(kitsOpts);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadCategories = async () => {
    try {
      const categoryListRes = await getCategories();
      setCategories(categoryListRes.data || []);
      const options = (categoryListRes.data || []).map(category => ({
        value: category.id,
        label: category.name,
        category: category
      }));
      setCategoryOptions(options);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadWarehouses = async () => {
    try {
      const response = await getWarehouses();
      if (response && response.data) {
        const options = response.data.map(warehouse => ({
          value: warehouse.id,
          label: warehouse.name
        }));
        setWarehouseOptions(options);
      }
    } catch (error) {
      console.error('Error loading warehouses:', error);
    } finally {
      setLoadingWarehouses(false);
    }
  };

  const loadAudiences = async () => {
    try {
      const response = await getAudiencesList({ status: 'active' });
      if (response && response.data) {
        const audiences = response.data.audiences || response.data || [];
        const options = audiences.map(aud => ({
          value: aud.id,
          label: `AUD-${aud.id} — ${aud.name}`,
          audience: aud,
        }));
        setAudienceOptions(options);
      }
    } catch (error) {
      console.error('Error loading audiences:', error);
    } finally {
      setLoadingAudiences(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCouponData({
      ...couponData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleProductSelection = (selectedOptions) => {
    const productIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setSelectedProducts(selectedOptions || []);
    setCouponData({
      ...couponData,
      applicable_product_ids: productIds
    });
  };

  const handleCategorySelection = (selectedOptions) => {
    const categoryIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setSelectedCategories(selectedOptions || []);
    setCouponData({
      ...couponData,
      applicable_category_ids: categoryIds
    });

    // Filter products based on selected categories
    if (categoryIds.length > 0) {
      // Check if products have categoryId or category_id field
      const filtered = productOptions.filter(option => {
        const product = option.product;
        return categoryIds.includes(product.categoryId);
      });
      setFilteredProductOptions(filtered);
      
      // Clear selected products if they don't belong to selected categories
      const validProducts = selectedProducts.filter(product => {
        const prod = product.product;
        return categoryIds.includes(prod.categoryId);
      });
      setSelectedProducts(validProducts);
      setCouponData(prev => ({
        ...prev,
        applicable_product_ids: validProducts.map(p => p.value)
      }));
    } else {
      setFilteredProductOptions(productOptions);
    }
  };

  const handleWarehouseSelection = (selectedOptions) => {
    const warehouseIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setSelectedWarehouses(selectedOptions || []);
    setCouponData({
      ...couponData,
      applicable_warehouse_ids: warehouseIds
    });
  };

  const handleKitSelection = () => {};
  // Kept as no-op for backward compatibility if referenced elsewhere

  const validateGSTNumber = (gstNumber) => {
    if (!gstNumber) return true; // Optional field
    
    // GST format: 2 digits (state code) + 10 alphanumeric (PAN) + 1 digit (entity number) + 1 letter (Z) + 1 alphanumeric (checksum)
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstRegex.test(gstNumber);
  };

  const handleGstNumberChange = (value) => {
    const upperValue = value.toUpperCase();
    setCouponData({
      ...couponData,
      gst_number: upperValue,
      applicable_gst_nums: upperValue.trim() ? [upperValue] : []
    });
  };

  const generateCouponCode = () => {
    const prefix = couponData.type === 'FIXED' ? 'ZUN' : 'OFF';
    // Use a CSPRNG (Web Crypto) instead of Math.random() for unpredictable codes
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const length = 6;
    const randomValues = new Uint32Array(length);
    window.crypto.getRandomValues(randomValues);
    let randomString = '';
    for (let i = 0; i < length; i++) {
      randomString += charset[randomValues[i] % charset.length];
    }
    const generatedCode = `${prefix}${randomString}`;
    setCouponData({
      ...couponData,
      code: generatedCode
    });
  };

  // Custom styles for react-select (same as CreateOrder)
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '42px',
      border: state.isFocused ? '2px solid #0d6efd' : '1px solid #ced4da',
      borderRadius: '6px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(13, 110, 253, 0.25)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#0d6efd' : '#ced4da'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#0d6efd' 
        : state.isFocused 
        ? '#f8f9fa' 
        : 'white',
      color: state.isSelected ? 'white' : '#212529',
      '&:hover': {
        backgroundColor: state.isSelected ? '#0d6efd' : '#f8f9fa'
      }
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: '9999 !important'
    }),
    menuPortal: base => ({
      ...base,
      zIndex: 9999
    })
  };

  const validateForm = () => {
    if (!couponData.code.trim()) {
      alert('Coupon code is required');
      return false;
    }
    
    if (!couponData.waive_off_delivery && (!couponData.value || parseFloat(couponData.value) <= 0)) {
      alert('Valid coupon value is required');
      return false;
    }
    
    if (!couponData.waive_off_delivery && couponData.type === 'PERCENT' && parseFloat(couponData.value) > 100) {
      alert('Percentage discount cannot exceed 100%');
      return false;
    }
    
    if (!couponData.valid_from || !couponData.valid_till) {
      alert('Valid from and valid till dates are required');
      return false;
    }
    
    if (new Date(couponData.valid_from) >= new Date(couponData.valid_till)) {
      alert('Valid till date must be after valid from date');
      return false;
    }
    
    if (couponData.min_cart_value && parseFloat(couponData.min_cart_value) < 0) {
      alert('Minimum cart value cannot be negative');
      return false;
    }
    
    if (couponData.max_discount && parseFloat(couponData.max_discount) < 0) {
      alert('Maximum discount cannot be negative');
      return false;
    }
    
    if (couponData.usage_limit && parseInt(couponData.usage_limit) < 1) {
      alert('Usage limit must be at least 1');
      return false;
    }

    if (couponData.valid_for_initial && parseInt(couponData.valid_for_initial) < 1) {
      alert('Valid for initial orders must be at least 1');
      return false;
    }

    if (couponData.gst_number && !validateGSTNumber(couponData.gst_number)) {
      alert('Invalid GST number format. Format: 22AAAAA0000A1Z5');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    try {
      // Prepare coupon data
      const newCoupon = {
        ...couponData,
        code: couponData.code.toUpperCase(),
        type: couponData.waive_off_delivery ? 'FIXED' : couponData.type,
        value: couponData.waive_off_delivery ? 0 : parseFloat(couponData.value),
        min_cart_value: couponData.min_cart_value ? parseFloat(couponData.min_cart_value) : null,
        max_discount: couponData.max_discount ? parseFloat(couponData.max_discount) : null,
        usage_limit: couponData.usage_limit ? parseInt(couponData.usage_limit) : null,
        valid_for_initial: couponData.valid_for_initial ? parseInt(couponData.valid_for_initial) : null,
        applicable_product_ids: couponData.applicable_product_ids.length > 0 ? couponData.applicable_product_ids : [],
        applicable_category_ids: couponData.applicable_category_ids.length > 0 ? couponData.applicable_category_ids : [],
        applicable_warehouse_ids: couponData.applicable_warehouse_ids.length > 0 ? couponData.applicable_warehouse_ids : [],
        applicable_gst_nums: couponData.applicable_gst_nums.length > 0 ? couponData.applicable_gst_nums : [],
        audience_id: selectedAudience ? selectedAudience.value : null,
        waive_off_delivery: couponData.waive_off_delivery,
      };

      const response = await createCoupon(newCoupon) || {};
      
      if(response.error) {
        alert(response.error || 'Some error occurred');
        return;
      }

      alert(`Coupon ${newCoupon.code} created successfully!`);
      navigate('/coupons');

    } catch (error) {
      console.error('Error creating coupon:', error);
      alert('Failed to create coupon. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="card">
        <div className="order-detail-header">
          <div className="order-detail-title">
            <div className="breadcrumb">
              <Link to="/coupons">Coupons</Link> / Create New Coupon
            </div>
            <h1>Create New Coupon</h1>
          </div>
          
          <div className="order-actions">
            <Link
              to="/coupons"
              className="btn btn-secondary"
              style={{ textDecoration: 'none' }}
            >
              Cancel
            </Link>
            <button
              type="submit"
              form="create-coupon-form"
              disabled={loading}
              className="btn btn-success"
            >
              {loading ? 'Creating...' : 'Create Coupon'}
            </button>
          </div>
        </div>

        <form id="create-coupon-form" onSubmit={handleSubmit}>
          {/* Basic Information */}
          <div className="card">
            <h3 className="section-title">Basic Information</h3>
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Coupon Code *</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    name="code"
                    className="form-control"
                    value={couponData.code}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter coupon code"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    style={{ textTransform: 'uppercase', flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={generateCouponCode}
                    className="btn btn-primary"
                    style={{ fontSize: '12px', padding: '8px 16px' }}
                  >
                    Generate
                  </button>
                </div>
              </div>
              
              <div className="info-item">
                <label className="info-label">Discount Type *</label>
                <select
                  name="type"
                  className="form-control"
                  value={couponData.type}
                  onChange={handleInputChange}
                  required
                  disabled={couponData.waive_off_delivery}
                  style={couponData.waive_off_delivery ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                >
                  <option value="FIXED">Fixed</option>
                  <option value="PERCENT">Percentage</option>
                </select>
              </div>
              
              <div className="info-item">
                <label className="info-label">
                  Discount Value * {couponData.type === 'FIXED' ? '(₹)' : '(%)'}
                </label>
                <input
                  type="number"
                  name="value"
                  className="form-control"
                  value={couponData.value}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step={couponData.type === 'FIXED' ? '0.01' : '1'}
                  max={couponData.type === 'PERCENT' ? '100' : undefined}
                  placeholder={couponData.waive_off_delivery ? 'N/A (Free Delivery)' : (couponData.type === 'FIXED' ? 'Enter amount' : 'Enter percentage')}
                  disabled={couponData.waive_off_delivery}
                  style={couponData.waive_off_delivery ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                />
              </div>
              
              <div className="info-item">
                <label className="info-label">Status</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    name="active"
                    checked={couponData.active}
                    onChange={handleInputChange}
                    style={{ width: 'auto' }}
                  />
                  <span>{couponData.active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>

              <div className="info-item">
                <label className="info-label">Free Delivery</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    name="waive_off_delivery"
                    checked={couponData.waive_off_delivery}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setCouponData(prev => ({
                        ...prev,
                        waive_off_delivery: checked,
                        ...(checked ? { type: 'FIXED', value: '0' } : {})
                      }));
                    }}
                    style={{ width: 'auto' }}
                  />
                  <span>{couponData.waive_off_delivery ? 'Yes - Delivery charges waived' : 'No'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Conditions */}
          <div className="card">
            <h3 className="section-title">Conditions & Limits</h3>
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Minimum Cart Value (₹)</label>
                <input
                  type="number"
                  name="min_cart_value"
                  className="form-control"
                  value={couponData.min_cart_value}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  placeholder="No minimum (optional)"
                />
              </div>
              
              <div className="info-item">
                <label className="info-label">Maximum Discount (₹)</label>
                <input
                  type="number"
                  name="max_discount"
                  className="form-control"
                  value={couponData.max_discount}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  placeholder="No limit (optional)"
                />
              </div>
              
              <div className="info-item">
                <label className="info-label">Usage Limit</label>
                <input
                  type="number"
                  name="usage_limit"
                  className="form-control"
                  value={couponData.usage_limit}
                  onChange={handleInputChange}
                  min="1"
                  placeholder="Unlimited (optional)"
                />
              </div>

              <div className="info-item">
                <label className="info-label">Valid for Initial Orders</label>
                <input
                  type="number"
                  name="valid_for_initial"
                  className="form-control"
                  value={couponData.valid_for_initial}
                  onChange={handleInputChange}
                  min="1"
                  placeholder="All orders (optional)"
                />
                <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Limit coupon to first N orders of a customer
                </small>
              </div>
            </div>
          </div>

          {/* Validity Period */}
          <div className="card">
            <h3 className="section-title">Validity Period</h3>
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Valid From *</label>
                <input
                  type="date"
                  name="valid_from"
                  className="form-control"
                  value={couponData.valid_from}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="info-item">
                <label className="info-label">Valid Till *</label>
                <input
                  type="date"
                  name="valid_till"
                  className="form-control"
                  value={couponData.valid_till}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          </div>

          {/* Applicable Products & Categories */}
          <div className="card">
            <h3 className="section-title">Applicable Products & Categories</h3>
            <p style={{ color: '#6c757d', marginBottom: '20px' }}>
              Select categories first to filter products, or leave empty to apply to all:
            </p>
            
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Select Categories</label>
                <Select
                  value={selectedCategories}
                  onChange={handleCategorySelection}
                  options={categoryOptions}
                  styles={selectStyles}
                  placeholder={loadingCategories ? 'Loading categories...' : 'Select categories...'}
                  isSearchable={true}
                  isClearable={true}
                  isMulti={true}
                  isLoading={loadingCategories}
                  isDisabled={loadingCategories}
                  noOptionsMessage={() => 'No categories found'}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                />
              </div>

              <div className="info-item">
                <label className="info-label">Select Products</label>
                <Select
                  value={selectedProducts}
                  onChange={handleProductSelection}
                  options={filteredProductOptions}
                  styles={selectStyles}
                  placeholder={loadingProducts ? 'Loading products...' : 'Select products...'}
                  isSearchable={true}
                  isClearable={true}
                  isMulti={true}
                  isLoading={loadingProducts}
                  isDisabled={loadingProducts}
                  noOptionsMessage={() => selectedCategories.length > 0 ? 'No products in selected categories' : 'No products found'}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                />
              </div>

            </div>
          </div>

          {/* Applicable Warehouses */}
          <div className="card">
            <h3 className="section-title">Applicable Warehouses</h3>
            <p style={{ color: '#6c757d', marginBottom: '20px' }}>
              Select warehouses where this coupon is valid, or leave empty to apply to all warehouses:
            </p>
            
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Select Warehouses</label>
                <Select
                  value={selectedWarehouses}
                  onChange={handleWarehouseSelection}
                  options={warehouseOptions}
                  styles={selectStyles}
                  placeholder={loadingWarehouses ? 'Loading warehouses...' : 'Select warehouses...'}
                  isSearchable={true}
                  isClearable={true}
                  isMulti={true}
                  isLoading={loadingWarehouses}
                  isDisabled={loadingWarehouses}
                  noOptionsMessage={() => 'No warehouses found'}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                />
                <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Empty selection means coupon is valid for all warehouses
                </small>
              </div>
            </div>
          </div>

          {/* GST Number */}
          <div className="card">
            <h3 className="section-title">B2B Customer Targeting</h3>
            <p style={{ color: '#6c757d', marginBottom: '20px' }}>
              Leave empty to apply to all customers, or specify a GST number for B2B targeting:
            </p>
            
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">GST Number</label>
                <input
                  type="text"
                  name="gst_number"
                  className="form-control"
                  value={couponData.gst_number || ''}
                  onChange={(e) => handleGstNumberChange(e.target.value)}
                  placeholder="22AAAAA0000A1Z5 (optional)"
                  maxLength="15"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  style={{ 
                    textTransform: 'uppercase',
                    borderColor: couponData.gst_number && !validateGSTNumber(couponData.gst_number) ? '#dc3545' : '#ced4da'
                  }}
                />
                {couponData.gst_number && !validateGSTNumber(couponData.gst_number) && (
                  <small style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Invalid GST format. Format: 22AAAAA0000A1Z5
                  </small>
                )}
              </div>
            </div>
          </div>

          {/* Audience Restriction */}
          <div className="card">
            <h3 className="section-title">Audience Restriction</h3>
            <p style={{ color: '#6c757d', marginBottom: '20px' }}>
              Optionally restrict this coupon to members of a specific audience. Non-members will see "This coupon is not available on your account."
            </p>
            
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Restrict to Audience (optional)</label>
                <Select
                  value={selectedAudience}
                  onChange={(option) => setSelectedAudience(option)}
                  options={audienceOptions}
                  styles={selectStyles}
                  placeholder={loadingAudiences ? 'Loading audiences...' : 'Select an audience (leave empty for public coupon)...'}
                  isSearchable={true}
                  isClearable={true}
                  isMulti={false}
                  isLoading={loadingAudiences}
                  isDisabled={loadingAudiences}
                  noOptionsMessage={() => 'No audiences found'}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                />
                <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Empty = public coupon (available to all users). Selecting an audience restricts this coupon to its members only.
                </small>
              </div>
            </div>
          </div>
          {isMobile && (
            <div className="m-sticky-actions">
              <Link
                to="/coupons"
                className="btn btn-secondary"
                style={{ textDecoration: 'none' }}
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={loading}
                className="btn btn-success"
              >
                {loading ? 'Creating...' : 'Create Coupon'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CreateCoupon;